<script generic="P, R" lang="ts" setup>
import type { ElSelectV2, LoadingInstance, SelectV2Props, } from "element-plus"
import type { OptionItem, Props, } from "./types"
import { ElLoading, } from "element-plus"
import { selectV2Props, } from "element-plus/es/components/select-v2/src/defaults"
import { isArray, omit, } from "lodash-es"
import { basePropsKeys, buildDefaultBaseProps, } from "./types"
import { useOptions, } from "./useOptions"

const props = defineProps({
  ...omit(selectV2Props, ["autocomplete", "options",],),
  ...buildDefaultBaseProps<P, R>(),
},)

const emits = defineEmits<{
  "change": [val: SelectV2Props["modelValue"],]
  "change-option": [option?: OptionItem | OptionItem[],]
  "paste": [val: PasteData,]
}>()

interface PasteData {
  e: ClipboardEvent
  options: OptionItem[]
  text?: string
}

const modelValue = defineModel<typeof props.modelValue>()

const { loading, formatOptions, fetchOptions, } = useOptions<P, R>(props as Props<P, R>,)

const componentRef = ref<InstanceType<typeof ElSelectV2>>()

const loadingInstance = ref<LoadingInstance>()

watch(() => loading.value, (val,) => {
  if (val) {
    const target = componentRef.value?.$el
    loadingInstance.value = ElLoading.service({
      target,
      customClass: "api-select-loading-mask",
    },)
  } else {
    loadingInstance.value?.close()
  }
},)

// 过滤掉自定义的props，只保留EL组件需要的props
const bindProps = computed(() => {
  return omit(props, basePropsKeys, "modelValue", "loading",)
},)

function handleChange(val: SelectV2Props["modelValue"],) {
  const { apiConfig, } = props
  if (apiConfig?.config?.value) {
    if (isArray(val,)) {
      const options = formatOptions.value.filter(item => val.includes(item.value,),)
      emits("change-option", options,)
    } else {
      const option = formatOptions.value.find(item => item.value === val,)
      emits("change-option", option,)
    }
  }
  emits("change", val,)
}

function handlePaste(e: ClipboardEvent,) {
  const { clipboardData, } = e
  if (clipboardData) {
    const text = clipboardData.getData("text",)
    emits("paste", { e, options: unref(formatOptions,), text, },)
  }
}

onMounted(() => {
  componentRef.value?.inputRef?.addEventListener("paste", handlePaste,)
},)

onBeforeUnmount(() => {
  componentRef.value?.inputRef?.removeEventListener("paste", handlePaste,)
},)

defineExpose({
  fetchOptions,
  formatOptions,
  componentRef,
},)
</script>

<template>
  <ElSelectV2
    ref="componentRef"
    v-model="modelValue"
    :loading="loading"
    :options="formatOptions"
    v-bind="bindProps"
    @change="handleChange"
  >
    <template v-for="(_, name) in $slots" #[name]="slotData">
      <slot :name="name" v-bind="slotData" />
    </template>
  </ElSelectV2>
</template>
